import React, { useState, useEffect, useRef, useCallback } from "react";
import ErrorMessage from "../ErrorMessage";
import Input from "component/form/Input";
import { CloseButton } from "react-bootstrap";
import { GreyTick } from "asset/icons";
import { useSelector } from "react-redux";
import Loader from "component/loader";
import {
    handleSendBusinessOtp,
    handleUpdateBusinessPhoneNumber,
    handleVerifyBusinessOtp,
} from "api/onboarding";

const PhoneVerificationModal = ({ phone, isOpen, onClose, onVerified }) => {
    const [otp, setOtp] = useState(["", "", "", ""]);
    const [error, setError] = useState("");
    const [timer, setTimer] = useState(120);
    const [isEditingPhone, setIsEditingPhone] = useState(false);
    const [editedPhone, setEditedPhone] = useState(phone);
    const currentBusinessId = useSelector((state) => state?.businessid);
    const [resendTrigger, setResendTrigger] = useState(0);

    const [resendDisabled, setResendDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const inputRefs = useRef([]);
    const formattedPhone = phone?.startsWith("+") ? phone : `+${phone}`;

    const handleResend = useCallback(
        async (phoneToSend) => {
            if (!phoneToSend) return;

            try {
                await handleSendBusinessOtp({
                    phoneNumber: phoneToSend.startsWith("+") ? phoneToSend : `+${phoneToSend}`,
                    businessId: currentBusinessId,
                });

                setTimer(120);
                setResendDisabled(true);
                setOtp(["", "", "", ""]);
                setResendTrigger((prev) => prev + 1);
                inputRefs.current[0]?.focus();
            } catch (error) {
                console.error("Failed to resend OTP:", error);
                setError("Failed to resend OTP. Please try again.");
            }
        },
        [currentBusinessId]
    );

    useEffect(() => {
        if (isOpen) {
            setTimer(120);
            setResendDisabled(true);
            setOtp(["", "", "", ""]);
            setError("");
            inputRefs.current[0]?.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && phone) {
            setEditedPhone(formattedPhone);
            handleResend(formattedPhone);
        }
    }, [formattedPhone, handleResend, isOpen, phone]);

    useEffect(() => {
        if (!isOpen) return;

        const interval = setInterval(() => {
            setTimer((prev) => {
                if (prev <= 1) {
                    setResendDisabled(false);
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isOpen, resendTrigger]);

    const handleOtpChange = (index, value) => {
        if (/^[0-9]?$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            setError("");

            if (value && index < 3) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleSubmit = async () => {
        const enteredOtp = otp.join("");
        if (enteredOtp.length < 4) {
            setError("Please enter all 4 digits.");
            return;
        }

        try {
            setLoading(true);
            setError("");

            const payload = {
                phoneNumber: editedPhone.startsWith("+") ? editedPhone : `+${editedPhone}`,
                businessId: currentBusinessId,
                otp: enteredOtp,
            };

            await handleVerifyBusinessOtp(payload);
            onVerified?.();
            onClose();
        } catch (err) {
            console.error("OTP verification failed:", err);
            setError("Incorrect code. Please check and try again.");
        } finally {
            setLoading(false);
        }
    };

    const handlePhoneChangeConfirm = async () => {
        if (!editedPhone || !currentBusinessId) return;

        try {
            await handleUpdateBusinessPhoneNumber(currentBusinessId, {
                phoneNumber: editedPhone.startsWith("+") ? editedPhone : `+${editedPhone}`,
                businessId: currentBusinessId,
            });

            setIsEditingPhone(false);
            setTimer(120);
            setResendDisabled(true);
            setOtp(["", "", "", ""]);
            setResendTrigger((prev) => prev + 1);
            inputRefs.current[0]?.focus();
        } catch (error) {
            console.error("Failed to update phone number:", error);
            setError("Failed to update phone number. Please try again.");
        }
    };

    const handlePhoneChangeCancel = () => {
        setEditedPhone(phone);
        setIsEditingPhone(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl w-full max-w-[600px] shadow-lg">
                <div className="p-8">
                    <h2 className="aku-heading-h2-medium text-[#101828]">Verify your phone number</h2>
                    <p className="text-[#667085] mt-2">
                        Enter the 4-digit code sent to{" "}
                        <span className="text-[#101828] font-bold">
                            <span className="text-[#101828] font-bold">
                                {editedPhone !== phone ? editedPhone : phone}
                            </span>
                        </span>{" "}
                        <button
                            type="button"
                            onClick={() => setIsEditingPhone(true)}
                            className="text-[#6F00FF] ml-1 font-medium"
                        >
                            Change
                        </button>
                    </p>

                    <div className="mt-6 flex space-x-2">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => (inputRefs.current[index] = el)}
                                type="text"
                                value={digit}
                                placeholder="-"
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                className="w-12 h-12 text-2xl text-center border border-gray-300 rounded-md focus:border-[#6F00FF] focus:ring-2 focus:ring-[#6F00FF] transition"
                                maxLength={1}
                            />
                        ))}
                    </div>

                    {error && <ErrorMessage message={error} />}

                    {resendDisabled ? (
                        <p className="text-[#667085] mt-4">
                            Resend code in{" "}
                            <span className="font-semibold text-[#101828]">
                                0{Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, "0")}
                            </span>
                        </p>
                    ) : (
                        <p className="text-[#667085] mt-4">
                            Didn’t receive code? Check WhatsApp or{" "}
                            <button
                                onClick={() => handleResend(formattedPhone)}
                                className="text-[#6F00FF] font-medium"
                            >
                                Resend code
                            </button>
                        </p>
                    )}
                </div>

                <div className="flex justify-end gap-4 border-t !px-8 py-3 items-center mt-6">
                    <button className="text-[#667085] font-medium" onClick={onClose}>
                        Skip. Verify later
                    </button>
                    <button
                        className="bg-[#6F00FF] text-[#FBFAFF] px-6 !py-3 rounded-lg font-semibold"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? <Loader /> : "Submit"}
                    </button>
                </div>
            </div>
            {isEditingPhone && (
                <div className="fixed inset-0 z-50 top-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="bg-[#F9FAFB] rounded-2xl -top-8 left-20 flex !px-2 !pb-2 !pr-6 gap-4 items-center w-full max-w-[300px] shadow-xl relative">
                        <Input
                            type="tel"
                            value={editedPhone}
                            onChange={(e) => setEditedPhone(e)}
                            placeholder="Enter phone number"
                        />
                        <div className="flex justify-end items-center gap-4">
                            <button
                                onClick={handlePhoneChangeConfirm}
                                className="text-[#6F00FF] text-xl"
                            >
                                <GreyTick />
                            </button>
                            <button onClick={handlePhoneChangeCancel}>
                                <CloseButton />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhoneVerificationModal;
